import { useQuery } from '@tanstack/react-query';
import { listAssignments, listSubmissions } from '../../learning/api/assignments';
import type { AssignmentProgressBubblyStats } from '../../../components/student/AssignmentProgressBubbly';
import { useTutorQueryIdentity } from '../../tutor/hooks/useTutorQueryIdentity';
import { assignmentDashboardStatsQueryKey } from '../queryKeys';
import { isDashboardIdentityReady } from '../utils/dashboardQueryGating';

export type AssignmentDashboardStats = AssignmentProgressBubblyStats & {
  pending: number;
  overdue: number;
};

export const ASSIGNMENT_DASHBOARD_STATS_QUERY_KEY = ['student-dashboard', 'assignment-stats'] as const;

const EMPTY_STATS: AssignmentDashboardStats = {
  total: 0,
  submitted: 0,
  reviewed: 0,
  recentFeedback: [],
  pending: 0,
  overdue: 0,
};

import { startOfLocalDay } from '../utils/dashboardDateUtils';

export async function loadAssignmentDashboardStats(
  grade: number | null | undefined,
): Promise<AssignmentDashboardStats> {
  const gradeParam = grade != null ? String(grade) : undefined;
  const [assignRes, subRes] = await Promise.all([
    listAssignments({ grade: gradeParam, limit: 100 }),
    listSubmissions({ limit: 100 }),
  ]);
  const subByAsn = new Map(subRes.items.map((s) => [s.assignment_id, s]));
  let submitted = 0;
  let reviewed = 0;
  let pending = 0;
  let overdue = 0;
  const today = startOfLocalDay();
  const recentFeedback: AssignmentDashboardStats['recentFeedback'] = [];

  for (const a of assignRes.items) {
    const sub = subByAsn.get(a.assignment_id);
    if (sub) {
      submitted++;
      if (sub.status === 'published' || sub.status === 'reviewed') {
        reviewed++;
        if (sub.grade != null && recentFeedback.length < 3) {
          recentFeedback.push({
            title: a.title,
            grade: sub.grade,
            feedback: sub.feedback ?? undefined,
            submittedAt: sub.submitted_at ?? sub.updated_at ?? sub.created_at ?? null,
          });
        }
      }
    } else if (a.due_date) {
      const due = new Date(a.due_date);
      if (!Number.isNaN(due.getTime()) && due < today) overdue++;
      else pending++;
    } else {
      pending++;
    }
  }

  return {
    total: assignRes.items.length,
    submitted,
    reviewed,
    recentFeedback,
    pending,
    overdue,
  };
}

/** Canonical owner for assignment + submission dashboard statistics. */
export function useAssignmentDashboardStats(
  grade: number | null | undefined,
  enabled: boolean,
  isRosterLoading: boolean,
) {
  const { membershipKey, studentKey } = useTutorQueryIdentity();
  const identityReady = isDashboardIdentityReady(membershipKey, studentKey);
  const queryEnabled = enabled && !isRosterLoading && identityReady;

  const queryKey = identityReady
    ? assignmentDashboardStatsQueryKey({ membershipKey, studentKey, grade })
    : [...ASSIGNMENT_DASHBOARD_STATS_QUERY_KEY, 'disabled'];

  const query = useQuery<AssignmentDashboardStats, Error>({
    queryKey,
    queryFn: () => loadAssignmentDashboardStats(grade),
    enabled: queryEnabled,
    staleTime: 5 * 60 * 1000,
  });

  return {
    stats: query.data ?? EMPTY_STATS,
    loading: queryEnabled && query.isLoading,
    error: query.error?.message ?? null,
    reload: () => void query.refetch(),
  };
}
